import { Box, SwipeableDrawer, SxProps } from '@mui/material';
import React, { useCallback, useMemo, useRef } from 'react';

import { KeyboardKeyEnum } from '../../../types/shell';

interface IProps {
  open: boolean;
  onClose: () => void;
  onOpen: (event: React.SyntheticEvent) => void;
  children: React.ReactNode;
  sx?: SxProps;
  /** Sheet max-height as a percentage of the viewport. */
  maxHeightPercent?: number;
}

// Swipe-down-to-close is handled natively by SwipeableDrawer, which defers to native scrolling
// while a scrollable body isn't at its top — so a mid-scroll drag never closes the sheet. That
// only holds when the body is a real overflow-y:auto scroller (as in ViewDetailsSidebar /
// AddressBookPicker).
export const BottomSheet = ({ open, onClose, onOpen, children, sx, maxHeightPercent = 80 }: IProps) => {
  const pointerDownYRef = useRef<number | null>(null);

  const handlePullerPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    pointerDownYRef.current = event.clientY;
  }, []);

  // The handle closes on tap; SwipeableDrawer only reacts to drags, so a tap needs its own
  // handler — but ignore pointer-ups that moved enough to be a swipe (that path already closes).
  const handlePullerClick = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const startY = pointerDownYRef.current;
      pointerDownYRef.current = null;
      if (startY !== null && Math.abs(event.clientY - startY) > 6) {
        return;
      }
      onClose();
    },
    [onClose],
  );

  const pullerComponent = useMemo(() => {
    return (
      <Box
        role="button"
        aria-label="Close"
        tabIndex={0}
        onPointerDown={handlePullerPointerDown}
        onPointerUp={handlePullerClick}
        onKeyDown={(event) => {
          if (event.key === KeyboardKeyEnum.Enter || event.key === KeyboardKeyEnum.Space) {
            onClose();
          }
        }}
        sx={{
          width: '100%',
          py: 2,
          cursor: 'grab',
          touchAction: 'none',
          display: 'flex',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 4,
            backgroundColor: 'action.active',
            borderRadius: 2,
          }}
        />
      </Box>
    );
  }, [handlePullerPointerDown, handlePullerClick, onClose]);

  const { rootSx, paperSx } = useMemo(() => {
    const sxObject = (sx && typeof sx === 'object' && !Array.isArray(sx) ? sx : {}) as Record<string, unknown>;
    const { '& .MuiDrawer-paper': customPaperStyles, ...restSx } = sxObject;

    return {
      rootSx: restSx,
      paperSx: {
        borderTopLeftRadius: 28,
        borderTopRightRadius: 28,
        maxHeight: `${maxHeightPercent}%`,
        overflow: 'visible',
        px: 2,
        pb: 2,
        ...(typeof customPaperStyles === 'object' && customPaperStyles !== null ? customPaperStyles : {}),
      },
    };
  }, [sx, maxHeightPercent]);

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={onOpen}
      disableSwipeToOpen
      sx={rootSx}
      slotProps={{ paper: { sx: paperSx } }}
    >
      {pullerComponent}
      {children}
    </SwipeableDrawer>
  );
};
